/* eslint-disable @typescript-eslint/no-explicit-any */
import { LocalDateTime } from 'js-joda';
import _ from 'lodash';

import { ActionsType, StoreType } from '@tock/shared/utils/connect';
import { BUSINESS_LOCK_DURATION } from 'utils/constants';
import { LockOptions } from 'types';
import { consumer, core, toJSON } from '@tock/coretypes';
import { isEventVariety } from '@tock/shared/utils/offerings';
import { refreshCalendar } from 'utils/refreshData';

function getUnlockRequests(fetchLockResponse: any) {
  return _.map(
    _.filter(fetchLockResponse.payload.lockedTicketSubset, (lock) => {
      // Ignore hand-sells from unlock request.
      return _.isNull(lock.ticketSubset.purchaseVerifyInfo);
    }),
    (lock) => {
      const lockedTickets = toJSON<core.LockedTicketSubset>(lock);
      return {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ticketSubsetId: lockedTickets.ticketSubset!.id,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ticketGroupId: lockedTickets.ticketSubset!.ticketGroupId,
      };
    }
  );
}

export function isLockOfEvent(lock: core.TicketGroup) {
  const variety = lock.ticketTypeVariety;
  return isEventVariety(variety);
}

export function removeLocks(
  lockActions: ActionsType['lock'],
  selectionActions: ActionsType['selection'],
  businessId?: number
) {
  return lockActions.fetchAll(businessId).then((fetchResponse: any) => {
    selectionActions.selectOptions(null);
    const unlockRequests = getUnlockRequests(fetchResponse);
    return Promise.all(
      _.map(unlockRequests, (request) => {
        lockActions.unlockTickets(request, businessId);
        lockActions.clear();
        return;
      })
    );
  });
}

export function getLockedDateTimeFor(
  currentLock: core.TicketGroup,
  ticketTypeId: number,
  size: number
) {
  const ticketSubset: core.ITicketSubset = _.first(currentLock.ticketSubset) || {};
  const currentLockTicketTypeId = currentLock.ticketType[0].id;
  const currentLockSize = ticketSubset.ticketCount;

  if (currentLockSize === size && currentLockTicketTypeId === ticketTypeId) {
    return ticketSubset.requestedDateTime;
  }
  return undefined;
}

export function getLockedSeatingAreaFor(
  currentLock: core.TicketGroup,
  ticketTypeId: number,
  size: number
): core.SeatingArea | undefined {
  const ticketSubset: core.ITicketSubset = _.first(currentLock.ticketSubset) || {};
  const currentLockTicketTypeId = currentLock.ticketType[0].id;
  const currentLockSize = ticketSubset.ticketCount;

  if (
    currentLockSize === size &&
    currentLockTicketTypeId === ticketTypeId &&
    ticketSubset.seatingArea?.id &&
    ticketSubset.seatingArea?.name
  ) {
    const { id, name } = ticketSubset.seatingArea;
    return {
      id,
      name,
    };
  }
  return undefined;
}

export function isLockedForSize(
  ticketTypeId: number,
  size?: number,
  currentLock?: core.TicketGroup
) {
  if (!_.isNil(currentLock) && !_.isNil(size)) {
    const currentLockTicketTypeId = currentLock.ticketType[0].id;
    const ticketSubset: core.ITicketSubset = _.first(currentLock.ticketSubset) || {};
    const currentLockSize = ticketSubset.ticketCount;

    if (currentLockSize === size && currentLockTicketTypeId === ticketTypeId) {
      return true;
    }
  }
  return false;
}

export async function tryLockAndContinue(
  actions: ActionsType,
  ticketTypeId: number,
  size: number,
  lock: StoreType['lock'],
  dateTime: LocalDateTime,
  priceCents: number | undefined,
  options: LockOptions,
  business: consumer.IBusinessConfig,
  purchaseId?: number,
  rescheduleToken?: string
): Promise<core.TicketGroup | undefined> {
  const selectionActions = actions.selection;
  const lockActions = actions.lock;
  const fromSyndicateRoute = /\/syndicate$/.test(window.location.pathname);
  let lockedDateTime;
  let lockedSeatingAreaId = options.seatingAreaId;
  if (lock.currentLock) {
    lockedDateTime = getLockedDateTimeFor(lock.currentLock, ticketTypeId, size);
    if (!fromSyndicateRoute) {
      lockedSeatingAreaId =
        getLockedSeatingAreaFor(lock.currentLock, ticketTypeId, size)?.id || undefined;
    }
  }

  const dateTimeString = dateTime.toString();

  if (lockedDateTime === dateTimeString) {
    return Promise.resolve(undefined);
  }

  selectionActions.selectTicketType(ticketTypeId);
  selectionActions.selectSize(size);
  selectionActions.selectDate(dateTime.toLocalDate().toString());
  selectionActions.selectTime(dateTime.toLocalTime().toString());

  try {
    const fetchResponse = await lockActions.fetchAll(business.id || undefined);
    const lockTicketsResp = await lockActions.lockTickets(
      {
        dateTime: dateTimeString,
        size,
        ticketTypeId,
        ticketTypePrice: priceCents,
        exclusivity: options.exclusivity,
        purchaseId,
        rescheduleToken,
        seatingAreaId: lockedSeatingAreaId,
      },
      business?.lockDurationMinutes ?? BUSINESS_LOCK_DURATION,
      options,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      business.id!
    );

    selectionActions.selectOptions(null);
    const unlockRequests = getUnlockRequests(fetchResponse);
    // Unlock asynchronously, no need to block the guest.
    setTimeout(
      () =>
        Promise.all(
          _.map(unlockRequests, (request) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return lockActions.unlockTickets(request, business.id!);
          })
        ),
      0
    );
    return lockTicketsResp.payload;
  } catch (err: any) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    refreshCalendar(actions, options.ticketTypeAccessToken, business.id!);
    throw err;
  }
}

export async function tryLockAndContinueCitySearch(
  actions: ActionsType,
  ticketTypeId: number,
  size: number,
  lock: StoreType['lock'],
  dateTime: LocalDateTime,
  priceCents: number,
  options: LockOptions,
  business: consumer.OfferingBusiness
) {
  const {
    lock: { lockTickets, clear, unlockTickets },
    selection,
  } = actions;
  const lockedDateTime = lock.currentLock
    ? getLockedDateTimeFor(lock.currentLock, ticketTypeId, size)
    : undefined;
  const dateTimeString = dateTime.toString();

  // trying to lock the same one
  if (lockedDateTime === dateTimeString) {
    return undefined;
  }

  if (lock.currentLock) {
    clear();
    unlockTickets(
      {
        ticketSubsetId: lock.currentLock.ticketSubset[0].id,
        ticketGroupId: lock.currentLock.ticketSubset[0].ticketGroupId,
      },
      business.id as number
    );
  }

  selection.selectTicketType(ticketTypeId);
  selection.selectSize(size);
  selection.selectDate(dateTime.toLocalDate().toString());
  selection.selectTime(dateTime.toLocalTime().toString());

  const lockResponse = await lockTickets(
    {
      dateTime: dateTimeString,
      size,
      ticketTypeId,
      ticketTypePrice: priceCents,
      exclusivity: options.exclusivity,
      seatingAreaId: options?.seatingAreaId,
    },
    BUSINESS_LOCK_DURATION,
    options,
    business.id,
    business.timeZone
  );

  return lockResponse;
}

export function getMultiCheckoutProgress(lock: StoreType['lock']) {
  const totalCompletedItems = lock.completedMultiCheckoutPurchaseIds?.length ?? 0;
  const totalCurrentItems = lock.currentLock ? 1 : 0;
  const totalRemainingItems = lock.remainingLocks?.length ?? 0;

  const totalItems = totalCompletedItems + totalCurrentItems + totalRemainingItems;
  const currentItemIndex = totalCompletedItems + totalCurrentItems;

  const totalWithoutCurrentLock = totalRemainingItems + totalCompletedItems;

  const isMultiCheckout = totalWithoutCurrentLock > 0;

  return { totalItems, currentItemIndex, isMultiCheckout };
}
